import React from 'react';
import { useFormikContext, FieldArray, getIn } from 'formik';
import i18n from 'i18n-js';
import Button from '@material-ui/core/Button';
import {
    Box,
    FormControl,
    IconButton,
    Select,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

import { FormikTimePicker, Input } from 'components/shared';
import { Positions, MenuType } from 'constants/enums';
import { useStyles } from '../../../styles';
import { useAppSelector } from 'index';
import { DaysSelectOptions } from '..';

export interface PriceManagementTabProps {
    disabled: boolean;
    isFieldDisabled: (name: string, additionalOption?: boolean) => boolean;
}

export default function PriceManagementTab({ disabled, isFieldDisabled }: PriceManagementTabProps) {
    const classes = useStyles();
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const user = useAppSelector((state) => state.user);
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const currentPosition = currentRestaurant?.currentPosition;
    const isCatalog = menu.type === MenuType.Catalog;
    const isCatalogManager =
        (menu.type === MenuType.Catalog &&
            currentPosition === Positions.MANAGER &&
            menu.catalogData?.allowToEditCatalog) ??
        false;

    return (
        <>
            {!!values.priceLevels.length && (
                <>
                    <Box component="h6" className={classes.dishFormSectionTitle}>
                        {i18n.t('common.setPriceLevels')}
                    </Box>
                    <Table className={classes.sizesAndPricesTable}>
                        <TableHead>
                            <TableRow>
                                {values.size && <TableCell></TableCell>}
                                {values.priceLevels.map((level: any) => {
                                    const label =
                                        restaurant.pricingLevels.find(
                                            (lev) => lev.id === level.priceLevelId
                                        )?.name || 'price level';
                                    return (
                                        <TableCell key={level.priceLevelId}>
                                            <label>{label}</label>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                {values.size && (
                                    <TableCell>
                                        <label>{values.size}</label>
                                    </TableCell>
                                )}
                                {values.priceLevels.map((level: any, index: number) => {
                                    const priceName = `priceLevels[${index}].price`;
                                    const priceError = getIn(errors, priceName);
                                    return (
                                        <TableCell key={index}>
                                            <Input
                                                disabled={
                                                    disabled ||
                                                    values.isCustomPrice ||
                                                    (isCatalog && !isCatalogManager)
                                                }
                                                variant="filled"
                                                fullWidth
                                                name={priceName}
                                                type="text"
                                                label={'CHF'}
                                                error={priceError}
                                                placeholder={'CHF'}
                                                style={{ marginBottom: 5 }}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {values.additionalSizes.map((additionalSize: any, index: number) => {
                                return (
                                    <TableRow key={index}>
                                        <TableCell>{additionalSize.size}</TableCell>
                                        {additionalSize.priceLevels.length
                                            ? additionalSize.priceLevels.map(
                                                  (priceLevel: any, priceIndex: number) => {
                                                      const levelLabel =
                                                          restaurant.pricingLevels.find(
                                                              (level) =>
                                                                  level.id ===
                                                                  priceLevel.priceLevelId
                                                          )?.name || 'additional price';
                                                      const priceName = `additionalSizes.[${index}].priceLevels.[${priceIndex}].price`;
                                                      const priceError = getIn(errors, priceName);
                                                      return (
                                                          <TableCell key={`${index}-${priceIndex}`}>
                                                              <Input
                                                                  fullWidth
                                                                  style={{
                                                                      marginRight: 5,
                                                                      marginBottom: 5,
                                                                  }}
                                                                  disabled={
                                                                      disabled ||
                                                                      values.isCustomPrice ||
                                                                      (isCatalog &&
                                                                          !isCatalogManager)
                                                                  }
                                                                  variant="filled"
                                                                  name={priceName}
                                                                  type="text"
                                                                  label={levelLabel}
                                                                  error={priceError}
                                                                  placeholder={levelLabel}
                                                              />
                                                          </TableCell>
                                                      );
                                                  }
                                              )
                                            : null}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </>
            )}

            {restaurant.isOrderInAdvance && (
                <>
                    <Box component="h6" className={classes.dishFormSectionTitle}>
                        {i18n.t('restaurant.orderInAdvance')}
                    </Box>
                    <Table className={classes.orderInAdvanceTable}>
                        <TableHead>
                            <TableRow>
                                {values.size && (
                                    <TableCell align="left">{i18n.t('common.size')}</TableCell>
                                )}
                                <TableCell align="left">{i18n.t('common.priceCHF')}</TableCell>
                                <TableCell align="left">{i18n.t('common.qty')}</TableCell>
                                <TableCell align="left">{i18n.t('common.dayAndTime')}</TableCell>
                                <TableCell align="left" style={{ padding: 10 }}>
                                    {i18n.t('common.pickupHours')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Main dish row */}
                            <TableRow>
                                {values.size && (
                                    <TableCell>
                                        <label>{values.size}</label>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Input
                                        variant="filled"
                                        name="orderInAdvanceSettings.price"
                                        type="text"
                                        label={i18n.t('form.price')}
                                        error={getIn(errors, 'orderInAdvanceSettings.price')}
                                        placeholder={i18n.t('form.price')}
                                        style={{ maxWidth: 80 }}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        variant="filled"
                                        name="orderInAdvanceSettings.stockQuantity"
                                        type="text"
                                        label={i18n.t('common.qty')}
                                        error={getIn(
                                            errors,
                                            'orderInAdvanceSettings.stockQuantity'
                                        )}
                                        placeholder={i18n.t('common.qty')}
                                        style={{ maxWidth: 80 }}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <FieldArray
                                        name="orderInAdvanceSettings.orderInAdvanceDaySettings"
                                        render={(arrayHelpers) => (
                                            <>
                                                {values.orderInAdvanceSettings?.orderInAdvanceDaySettings?.map(
                                                    (dayData: any, index: number) => {
                                                        const from = `orderInAdvanceSettings.orderInAdvanceDaySettings[${index}].availability.from`;
                                                        const to = `orderInAdvanceSettings.orderInAdvanceDaySettings[${index}].availability.to`;
                                                        const day = `orderInAdvanceSettings.orderInAdvanceDaySettings[${index}].day`;
                                                        return (
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="flex-start"
                                                                paddingRight={1}
                                                                marginBottom={1}
                                                                key={index}
                                                            >
                                                                <FormControl
                                                                    variant="filled"
                                                                    size="small"
                                                                    style={{
                                                                        marginRight: 10,
                                                                        maxWidth: 100,
                                                                    }}
                                                                >
                                                                    <Select
                                                                        value={dayData.day}
                                                                        onChange={(e) =>
                                                                            setFieldValue(
                                                                                day,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    >
                                                                        {DaysSelectOptions.map(
                                                                            (dayOption) => (
                                                                                <MenuItem
                                                                                    key={
                                                                                        dayOption.value
                                                                                    }
                                                                                    value={
                                                                                        dayOption.value
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        dayOption.label
                                                                                    }
                                                                                </MenuItem>
                                                                            )
                                                                        )}
                                                                    </Select>
                                                                </FormControl>
                                                                <FormikTimePicker
                                                                    name={from}
                                                                    size="small"
                                                                    required
                                                                    style={{
                                                                        marginBottom: 0,
                                                                        marginRight: 5,
                                                                    }}
                                                                />
                                                                {' - '}
                                                                <FormikTimePicker
                                                                    name={to}
                                                                    size="small"
                                                                    required
                                                                    style={{
                                                                        marginBottom: 0,
                                                                        marginLeft: 5,
                                                                    }}
                                                                />
                                                                <IconButton
                                                                    aria-label="pen"
                                                                    size="small"
                                                                    onClick={() =>
                                                                        arrayHelpers.remove(index)
                                                                    }
                                                                    style={{ marginLeft: 5 }}
                                                                >
                                                                    <RemoveCircleOutlineIcon
                                                                        fontSize="small"
                                                                        style={{
                                                                            color: 'rgba(0, 0, 0, 0.54)',
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </Box>
                                                        );
                                                    }
                                                )}
                                                <Button
                                                    variant="text"
                                                    color="primary"
                                                    onClick={() => {
                                                        arrayHelpers.push({
                                                            day: 'sunday',
                                                            availability: {
                                                                from: null,
                                                                to: null,
                                                            },
                                                        });
                                                    }}
                                                >
                                                    {`+ ${i18n.t('common.addMore').toUpperCase()}`}
                                                </Button>
                                            </>
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <FormikTimePicker
                                            name="orderInAdvanceSettings.pickupTime.from"
                                            size="small"
                                            style={{
                                                marginBottom: 0,
                                                marginRight: 5,
                                            }}
                                        />
                                        {' - '}
                                        <FormikTimePicker
                                            name="orderInAdvanceSettings.pickupTime.to"
                                            size="small"
                                            style={{
                                                marginBottom: 0,
                                                marginLeft: 5,
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>

                            {/* Additional sizes rows */}
                            {values.additionalSizes.map(
                                (additionalSize: any, sizeIndex: number) => {
                                    const priceName = `additionalSizes[${sizeIndex}].orderInAdvanceSettings.price`;
                                    const stockName = `additionalSizes[${sizeIndex}].orderInAdvanceSettings.stockQuantity`;
                                    return (
                                        <TableRow key={sizeIndex}>
                                            <TableCell>{additionalSize.size}</TableCell>
                                            <TableCell>
                                                <Input
                                                    variant="filled"
                                                    name={priceName}
                                                    type="text"
                                                    label={i18n.t('form.price')}
                                                    error={getIn(errors, priceName)}
                                                    placeholder={i18n.t('form.price')}
                                                    style={{ maxWidth: 80 }}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    variant="filled"
                                                    name={stockName}
                                                    type="text"
                                                    label={i18n.t('common.qty')}
                                                    error={getIn(errors, stockName)}
                                                    placeholder={i18n.t('common.qty')}
                                                    style={{ maxWidth: 80 }}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FieldArray
                                                    name={`additionalSizes[${sizeIndex}].orderInAdvanceSettings.orderInAdvanceDaySettings`}
                                                    render={(arrayHelpers) => (
                                                        <>
                                                            {values.additionalSizes[
                                                                sizeIndex
                                                            ]?.orderInAdvanceSettings?.orderInAdvanceDaySettings?.map(
                                                                (
                                                                    dayData: any,
                                                                    dayIndex: number
                                                                ) => {
                                                                    const from = `additionalSizes[${sizeIndex}].orderInAdvanceSettings.orderInAdvanceDaySettings[${dayIndex}].availability.from`;
                                                                    const to = `additionalSizes[${sizeIndex}].orderInAdvanceSettings.orderInAdvanceDaySettings[${dayIndex}].availability.to`;
                                                                    const day = `additionalSizes[${sizeIndex}].orderInAdvanceSettings.orderInAdvanceDaySettings[${dayIndex}].day`;
                                                                    return (
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            justifyContent="flex-start"
                                                                            paddingRight={1}
                                                                            marginBottom={1}
                                                                            key={dayIndex}
                                                                        >
                                                                            <FormControl
                                                                                variant="filled"
                                                                                size="small"
                                                                                style={{
                                                                                    marginRight: 10,
                                                                                    maxWidth: 100,
                                                                                }}
                                                                            >
                                                                                <Select
                                                                                    value={
                                                                                        dayData.day
                                                                                    }
                                                                                    onChange={(e) =>
                                                                                        setFieldValue(
                                                                                            day,
                                                                                            e.target
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {DaysSelectOptions.map(
                                                                                        (
                                                                                            dayOption
                                                                                        ) => (
                                                                                            <MenuItem
                                                                                                key={
                                                                                                    dayOption.value
                                                                                                }
                                                                                                value={
                                                                                                    dayOption.value
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    dayOption.label
                                                                                                }
                                                                                            </MenuItem>
                                                                                        )
                                                                                    )}
                                                                                </Select>
                                                                            </FormControl>
                                                                            <FormikTimePicker
                                                                                name={from}
                                                                                size="small"
                                                                                required
                                                                                style={{
                                                                                    marginBottom: 0,
                                                                                    marginRight: 5,
                                                                                }}
                                                                            />
                                                                            {' - '}
                                                                            <FormikTimePicker
                                                                                name={to}
                                                                                size="small"
                                                                                required
                                                                                style={{
                                                                                    marginBottom: 0,
                                                                                    marginLeft: 5,
                                                                                }}
                                                                            />
                                                                            <IconButton
                                                                                aria-label="pen"
                                                                                size="small"
                                                                                onClick={() =>
                                                                                    arrayHelpers.remove(
                                                                                        dayIndex
                                                                                    )
                                                                                }
                                                                                style={{
                                                                                    marginLeft: 5,
                                                                                }}
                                                                            >
                                                                                <RemoveCircleOutlineIcon
                                                                                    fontSize="small"
                                                                                    style={{
                                                                                        color: 'rgba(0, 0, 0, 0.54)',
                                                                                    }}
                                                                                />
                                                                            </IconButton>
                                                                        </Box>
                                                                    );
                                                                }
                                                            )}
                                                            <Button
                                                                variant="text"
                                                                color="primary"
                                                                onClick={() => {
                                                                    arrayHelpers.push({
                                                                        day: 'sunday',
                                                                        availability: {
                                                                            from: null,
                                                                            to: null,
                                                                        },
                                                                    });
                                                                }}
                                                            >
                                                                {`+ ${i18n
                                                                    .t('common.addMore')
                                                                    .toUpperCase()}`}
                                                            </Button>
                                                        </>
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <FormikTimePicker
                                                        name={`additionalSizes[${sizeIndex}].orderInAdvanceSettings.pickupTime.from`}
                                                        size="small"
                                                        required
                                                        style={{
                                                            marginBottom: 0,
                                                            marginRight: 5,
                                                        }}
                                                    />
                                                    {' - '}
                                                    <FormikTimePicker
                                                        name={`additionalSizes[${sizeIndex}].orderInAdvanceSettings.pickupTime.to`}
                                                        size="small"
                                                        required
                                                        style={{
                                                            marginBottom: 0,
                                                            marginLeft: 5,
                                                        }}
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            )}
                        </TableBody>
                    </Table>
                </>
            )}
        </>
    );
}
